import React, { useState, useEffect, useRef } from 'react'
import { Form, InputGroup } from 'react-bootstrap'
import { useFetch, usePost, usePut, notify } from '../component-items/helpers'
import GlobalStyles from '../component-items/styles'
import GenericModal from '../component-items/modal'
import Checkbox from '../component-items/checkbox'
import { UserType } from './RoleBadge'
import { PermissionGate } from '../rbac'

export interface UserData {
  id?: number | null
  first_name: string
  last_name: string
  email: string
  username: string
  is_active?: boolean
  has_2fa?: boolean
  role_id?: string
  is_admin?: boolean
}

interface EditUserModalProps {
  currentUser: Partial<UserData>
  open: boolean
  setOpen: (open: boolean) => void
  userType: UserType
  /** For warehouse users */
  warehouseId?: number
  /** For merchant users */
  companyId?: number
  csrf: string
  /** Whether the current session is in impersonation mode (read-only) */
  isImpersonating?: boolean
}

const defaultUser: UserData = {
  id: null,
  first_name: '',
  last_name: '',
  email: '',
  username: '',
  is_active: true,
  has_2fa: false,
  role_id: '',
  is_admin: false,
}

export const EditUserModal = ({
  currentUser,
  open,
  setOpen,
  userType,
  warehouseId,
  companyId,
  csrf,
  isImpersonating = false,
}: EditUserModalProps) => {
  const [validated, setValidated] = useState(false)
  const [user, setUser] = useState<UserData>(defaultUser)
  const node = useRef<HTMLFormElement | null>(null)

  // Build roles URL based on user type
  const rolesUrl =
    userType === 'warehouse'
      ? `/api/rbac/roles/assignable/?warehouse_id=${warehouseId}`
      : `/api/rbac/roles/assignable/?company_id=${companyId}`

  // Fetch assignable roles for dropdown (filtered by user's level)
  const { response: rolesResp, loaded: rolesLoaded }: any = useFetch(rolesUrl, {})
  const roles = rolesLoaded && Array.isArray(rolesResp) ? [...rolesResp].sort((a, b) => b.level - a.level) : []

  useEffect(() => {
    if (Object.keys(currentUser).length === 0 && currentUser.constructor === Object) {
      setUser(defaultUser)
    } else {
      setUser({ ...defaultUser, ...currentUser })
    }
  }, [currentUser])

  // Build API URLs based on user type
  const getApiUrl = (userId?: number | null) => {
    if (userType === 'warehouse') {
      return userId ? `/api/warehouse/${warehouseId}/users/${userId}/` : `/api/warehouse/${warehouseId}/users/`
    }
    return userId ? `/api/company/${companyId}/user/${userId}/` : `/api/company/${companyId}/user/`
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setValidated(true)

    if (node?.current?.checkValidity() === false) {
      return
    }

    if (user.id) {
      usePut(getApiUrl(user.id), user, csrf)
    } else {
      usePost(getApiUrl(), user, csrf)
    }
  }

  const handleResetTwoFactorAuth = async (event: React.MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()

    if (userType !== 'warehouse' || !user.id) return

    notify({
      type: 'warning',
      message: `Are you sure you want to remove two-factor authentication for ${user.username}?`,
      requestConfirmation: true,
      onSubmit: () => usePost(`/api/warehouse/${warehouseId}/users/${user.id}/deactivate_2fa/`, {}, csrf),
      autoClose: false,
    })
  }

  return (
    <GenericModal
      heading={user.id ? 'Update User' : 'Create User'}
      show={open}
      onHide={() => setOpen(false)}
      buttons={
        <GlobalStyles.Button data-pw="user-submit-btn" onClick={handleSubmit} disabled={isImpersonating}>
          {user.id ? 'Update' : 'Create'}
        </GlobalStyles.Button>
      }
    >
      <Form ref={node} noValidate validated={validated} style={{ margin: '2em' }} data-pw="user-form">
        {isImpersonating && (
          <div
            style={{
              background: '#fff3e0',
              padding: '0.75em 1em',
              borderRadius: '4px',
              fontSize: '0.9em',
              color: '#e65100',
              marginBottom: '1em',
            }}
          >
            <strong>Read-Only Mode:</strong> User modifications are not allowed during impersonation.
          </div>
        )}
        <fieldset disabled={isImpersonating}>
          <Form.Group className="required">
            <Form.Label>First Name</Form.Label>
            <Form.Control
              required
              type="text"
              data-pw="user-first-name"
              value={user.first_name}
              onChange={(e) => setUser({ ...user, first_name: e.target.value })}
            />
            <Form.Control.Feedback type="invalid">Please enter a valid first name.</Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="required">
            <Form.Label style={{ marginTop: '1em' }}>Last Name</Form.Label>
            <Form.Control
              required
              type="text"
              data-pw="user-last-name"
              value={user.last_name}
              onChange={(e) => setUser({ ...user, last_name: e.target.value })}
            />
            <Form.Control.Feedback type="invalid">Please enter a valid last name.</Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="required">
            <Form.Label style={{ marginTop: '1em' }}>Email</Form.Label>
            <Form.Control
              required
              type="email"
              data-pw="user-email"
              value={user.email}
              onChange={(e) => setUser({ ...user, email: e.target.value })}
            />
            <Form.Control.Feedback type="invalid">Please enter a valid email.</Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="required">
            <Form.Label style={{ marginTop: '1em' }}>Username</Form.Label>
            <Form.Control
              required
              type="text"
              data-pw="user-username"
              value={user.username}
              onChange={(e) => setUser({ ...user, username: e.target.value })}
            />
            <Form.Control.Feedback type="invalid">Please enter a valid username.</Form.Control.Feedback>
          </Form.Group>

          {!user.id && (
            <Form.Group style={{ marginTop: '1em' }}>
              <Form.Label>Role</Form.Label>
              <Form.Select
                data-pw="user-role"
                value={user.role_id || ''}
                onChange={(e) => setUser({ ...user, role_id: e.target.value })}
              >
                <option value="">Select a role...</option>
                {roles.map((role: any) => (
                  <option key={role.id} value={role.id}>
                    {role.name}
                  </option>
                ))}
              </Form.Select>
              <Form.Text className="text-muted">
                Assign an initial role to this user. You can modify permissions later.
              </Form.Text>
            </Form.Group>
          )}

          {/* Warehouse-specific: Reset 2FA button (settings admin only) */}
          {userType === 'warehouse' && user.id && user.has_2fa && (
            <PermissionGate segment="settings" minLevel="admin">
              <InputGroup style={{ marginTop: '1em' }}>
                <GlobalStyles.Button className="secondary" style={{ margin: 0 }} onClick={handleResetTwoFactorAuth}>
                  Reset Two-Factor Authentication
                </GlobalStyles.Button>
              </InputGroup>
            </PermissionGate>
          )}
        </fieldset>
      </Form>
    </GenericModal>
  )
}
